import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { AccountTypes, FlowTypes, RejectedAccount, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import {
  closeSpinner,
  External,
  Go,
  LogError,
  LogInfo,
  State,
  TrackProcessError,
} from '@store/index';
import { selectMultiInternalTransferEligibility } from '@store/internal-transfers/selectors/eligibility/eligibility.selectors';
import { selectMultiITTriageTransferFrom } from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import {
  selectClientAccountsLoaded,
  selectClientHasVCAccounts,
  shouldOrgTransferContinue,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  selectNonVCOrgTransferToggle,
  selectVCOrgToOrgToggle,
} from '@store/selectors/feature/feature.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, take, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InternalTransferMultiEligibilityGuard implements CanActivate {
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    return this.globalStore.pipe(
      // Ensure that the client accounts are loaded before proceeding with eligibility check
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      take(1),
      // Check the client's eligibility with data from the store, and return that along with the from accounts
      withLatestFrom(
        this.globalStore.select(selectMultiInternalTransferEligibility),
        this.globalStore.select(selectMultiITTriageTransferFrom),
        this.globalStore.select(selectNonVCOrgTransferToggle),
        this.globalStore.select(selectVCOrgToOrgToggle),
        this.globalStore.select(selectClientHasVCAccounts),
      ),
      map(
        ([
          ,
          payload,
          transferFrom,
          nonVcOrgTransferToggle,
          vcOrgToOrgToggle,
          clientHasVCAccounts,
        ]) => {
          // ORG-from Intelledox routing — moved here from the Multi-IT Summary
          // Continue effect so the decision runs only after
          // `/secure/client-accounts` has resolved (VC-account ownership required
          // for the check).
          //   NON_VC ON                              → continue in the flow
          //   NON_VC OFF, VC ON, client has VC       → continue in the flow
          //   Everything else (both OFF, or VC-only ON without VC accounts) → Intelledox
          if (
            transferFrom === AccountTypes.ORG &&
            !shouldOrgTransferContinue(
              nonVcOrgTransferToggle,
              vcOrgToOrgToggle,
              clientHasVCAccounts,
            )
          ) {
            const config = getConfigClientFromRTE();
            const intelledoxURL =
              config.pretty_ecs_domain + config.internal_transfer_intelledox_endpoint;
            this.globalStore.dispatch(
              LogInfo({
                payload: {
                  eventId: 'internal-transfer-multi-guard--org-routed-to-intelledox',
                  message: `Internal Transfer Multi Eligibility Guard routed ORG-from to Intelledox for ${FlowTypes.INTERNAL_TRANSFER}`,
                  code: LoggerCodes.GENERAL_SUCCESS,
                  data: {
                    nonVcOrgTransferToggle,
                    vcOrgToOrgToggle,
                    clientHasVCAccounts,
                  },
                },
              }),
            );
            this.globalStore.dispatch(External({ url: intelledoxURL }));
            this.globalStore.dispatch(closeSpinner());
            return false;
          }
          // If the client is eligible, update display accounts + log + close loading spinner and then activate page
          if (payload.isInternalTransferEligible) {
            if (!payload.fromAccounts) {
              throw new Error('BAD_PAYLOAD_FROM_ACCOUNTS');
            }
            this.globalStore.dispatch(
              LogInfo({
                payload: {
                  eventId: 'internal-transfer-multi-guard--eligible-accounts-found',
                  message: `Internal Transfer Multi Eligibility Guard eligibility success ${FlowTypes.INTERNAL_TRANSFER} ${payload.transferFromType} account`,
                  eligibleFromType: payload.transferFromType,
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            );
            this.globalStore.dispatch(closeSpinner());
            // If the client is ineligible, go to the ineligible page
          } else {
            this.globalStore.dispatch(
              Go({ path: [`${urlMapper[FlowTypes.INTERNAL_TRANSFER]}/ineligible`] }),
            );

            // Only FROM account ineligibility is possible (TO accounts are always eligible)
            const message = payload.ineligibleFromAccountType
              ? `No eligible IT ${payload.ineligibleFromAccountType} FROM account`
              : 'No eligible internal transfer accounts found';

            if (payload.ineligibleFromAccountType) {
              this.globalStore.dispatch(
                TrackProcessError({
                  errorCode: `coo-it-ineligible-from-${payload.ineligibleFromAccountType}_accounts`,
                }),
              );
            } else {
              // Track generic eligibility failure when account type is unknown
              this.globalStore.dispatch(
                TrackProcessError({
                  errorCode: 'coo-it-ineligible-unknown',
                }),
              );
            }

            this.globalStore.dispatch(
              LogError({
                payload: {
                  eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                  message,
                  ineligibleFromAccountType: payload.ineligibleFromAccountType,
                  flowType: FlowTypes.INTERNAL_TRANSFER,
                  rejectedFromAccounts: this.maskRejectedAccountsPII(
                    payload.rejectedFromAccounts || [],
                  ),
                  rejectedToAccounts: this.maskRejectedAccountsPII(
                    payload.rejectedToAccounts || [],
                  ),
                },
              }),
            );
          }
          return payload.isInternalTransferEligible;
        },
      ),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'internal-transfer-multi-guard--unknown-error',
              message: `Internal Transfer Multi Eligibility Guard failed due to unknown error. ${err instanceof Error ? err.message : String(err)}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-it-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }

  private maskRejectedAccountsPII(
    rejectedAccounts: RejectedAccount[],
  ): Pick<RejectedAccount, 'accountNumber' | 'accountType' | 'rejectionReason'>[] {
    return rejectedAccounts.map((account) => ({
      accountNumber:
        account.accountNumber && account.accountNumber.length > 0
          ? account.accountNumber.slice(-4).padStart(account.accountNumber.length, '*')
          : 'UNKNOWN',
      accountType: account.accountType,
      rejectionReason: account.rejectionReason,
    }));
  }
}
